<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 class="modal-title">Book Now</h4>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div class="modal-body">
            <template v-if="bookingStatus !== 'pending'">
                <form-wrapper :validator="$v.form" class="form-wrapper">
                    <form-group name="shippingDate" label="Shipping Date">
                        <md-input
                            type="datetime-local"
                            v-model="form.shippingDate"
                            :min="minDate"
                            @keydown.prevent
                            required
                        ></md-input>
                    </form-group>
                    <form-group name="contactName" label="Contact Name">
                        <md-input v-model="form.contactName" required></md-input>
                    </form-group>
                    <form-group name="contactEmail" label="Contact Email">
                        <md-input v-model="form.contactEmail" type="email" required></md-input>
                    </form-group>
                    <form-group name="contactPhone" label="Phone Number">
                        <md-input v-model="form.contactPhone" required></md-input>
                    </form-group>
                </form-wrapper>
            </template>
            <template v-else>
                <skeleton></skeleton>
                <skeleton></skeleton>
                <skeleton></skeleton>
                <skeleton></skeleton>
                <skeleton></skeleton>
                <skeleton></skeleton>
            </template>
        </div>
        <div class="modal-footer">
            <template v-if="bookingStatus !== 'pending'">
                <md-button class="dialog-button md-primary" @click.stop="bookNow">
                    Book
                </md-button>
                <md-button class="dialog-button md-default" @click.stop="$modal.hide">Cancel</md-button>
            </template>
            <template v-else>
                <skeleton width="50px"></skeleton>
                <skeleton width="50px"></skeleton>
            </template>
        </div>
    </div>
</template>
<script>
import { required, email } from 'vuelidate/lib/validators';
import { handleRequests, isPhoneValid } from '@/helpers';
import Skeleton from '@/pages/Orders/components/Skeleton';
import { GeneralMixin } from '@/mixins';
import moment from 'moment/moment';

export default {
    name: 'BookOrdersToCarrierModal',
    components: { Skeleton },
    props: {
        orderIds: {
            type: Array,
            default: () => []
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        initialContactName: {
            type: String,
            default: ''
        },
        initialContactEmail: {
            type: String,
            default: ''
        },
        initialPhone: {
            type: String,
            default: ''
        }
    },
    mixins: [GeneralMixin],
    data() {
        return {
            form: {
                shippingDate: this.getTomorrowDate(),
                contactName: this.initialContactName,
                contactEmail: this.initialContactEmail,
                contactPhone: this.initialPhone
            },
            shippingDate: this.getTomorrowDate(),
            /**
             * state-machine for booking status
             * |> idle <-> pending
             */
            bookingStatus: 'idle'
        };
    },
    validations: {
        form: {
            shippingDate: {
                required
            },
            contactName: {
                required
            },
            contactEmail: {
                required,
                email
            },
            contactPhone: {
                required,
                phoneValid: isPhoneValid
            }
        }
    },
    computed: {
        minDate() {
            // must return tomorrow utc 0 offset using moment
            return moment()
                .add(1, 'days')
                .format('YYYY-MM-DDTHH:mm');
        }
    },
    methods: {
        getTomorrowDate() {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(9, 0, 0, 0); // Default to 9 AM tomorrow
            return tomorrow.toISOString().slice(0, 16);
        },
        async bookNow() {
            // Validate form before proceeding
            this.$v.form.$touch();
            if (this.$v.form.$invalid) {
                // Create a more specific error message
                const errors = [];
                if (this.$v.form.shippingDate.$invalid) {
                    errors.push('Shipping Date is required');
                }
                if (this.$v.form.contactName.$invalid) {
                    errors.push('Contact Name is required');
                }
                if (this.$v.form.contactEmail.$invalid) {
                    if (!this.$v.form.contactEmail.required) {
                        errors.push('Contact Email is required');
                    } else if (!this.$v.form.contactEmail.email) {
                        errors.push('Contact Email must be a valid email address');
                    }
                }
                if (this.$v.form.contactPhone.$invalid) {
                    if (!this.$v.form.contactPhone.required) {
                        errors.push('Contact Phone is required');
                    } else if (!this.$v.form.contactPhone.phoneValid) {
                        errors.push('Contact Phone must be a valid phone number');
                    }
                }

                const message = errors.length > 0 ? errors.join(', ') : 'Please fill in all required fields correctly.';

                this.$notifyError(message);
                return;
            }

            this.bookingStatus = 'pending';
            try {
                const endpoint = '/api/carrier-jobs/book-to-carrier';
                const request = {
                    orderIds: this.orderIds,
                    shippingDate: this.form.shippingDate,
                    contactName: this.form.contactName,
                    contactEmail: this.form.contactEmail,
                    contactPhone: this.form.contactPhone
                };
                const response = await handleRequests(endpoint, {
                    method: 'POST',
                    data: request
                });
                if (response.status === 200) {
                    const {
                        data: { bookedCarrierJobs: data }
                    } = response;
                    this.resolve({ status: 'ok', data });
                    return;
                }
                throw new Error('Failed to book order/s to this carrier.');
            } catch (error) {
                const { data, status } = error;

                if (status === 400 && data && data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                    this.$notifyError(data.errors.join('<br>'));

                    // Return error status to the calling component
                    this.resolve({ status: 'error', error });
                    return;
                }

                this.$notifyError(data?.userFriendlyErrorMessage ?? 'Failed to book order/s to this carrier.');

                // Return error status to the calling component
                this.resolve({ status: 'error', error });
            } finally {
                this.bookingStatus = 'idle';
            }
        }
    }
};
</script>

<style scoped lang="scss"></style>
